<template>
  <div :class="{'mb-6' : hasValidation}" v-bind="attrs">
    <bf-label v-if="label"
              class="block mb-2 text-sm font-medium text-text-primary dark:text-white"
              :label="label"
              :loading="loading"/>
    <div class="flex relative">
      <slot name="prefixGroup"/>
      <div v-if="$slots.prefix"
           class="absolute flex h-full pointer-events-none ps-3 items-center justify-center">
        <slot name="prefix"/>
      </div>
      <skeleton-loader v-if="loading" class="text-sm block w-full rounded-lg z-10" :class="sizeClasses"/>

      <input v-else
             ref="inputRef"
             :type="type"
             v-bind="attrs"
             :aria-label="label"
             :value="modelValue"
             :disabled="disabled"
             :readonly="readonly"
             :class="[inputClasses, sizeClasses, readonlyClass]"
             :placeholder="placeholder"
             :required="required"
             @input="emitInput"
             @blur="emitBlur">

      <div v-if="$slots.suffix && !loading" class="absolute flex h-full pr-3 items-center justify-center right-0">
        <slot name="suffix"/>
      </div>

      <slot/>
    </div>

    <transition name="fade-from-top">
      <div v-if="$slots.validationMessage" class="mt-0.5 font-light text-xs text-red-900">
        <slot name="validationMessage"/>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import type {PropType} from "vue";

const $emit = defineEmits(['update:modelValue', 'blur'])
const slots = defineSlots()
const props = defineProps({
  modelValue: {
    type: null as unknown as PropType<string | null | undefined | number>,
    required: true,
  },
  label: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'md',
  },
  type: {
    type: String,
    default: 'text',
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  hasValidation: {
    type: Boolean,
    default: true
  },
  validationStatus: {
    type: String,
    default: '',
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  }
})

// set the input element as input ref
const inputRef = ref<HTMLInputElement>()

// if autofocus is true, focus the input
onMounted(async () => {
  await nextTick();
  if (props.autofocus) {
    focus();
  }
})

defineOptions({
  inheritAttrs: false
})
const attrs = useAttrs()

// Emit events
const emitInput = (event: Event) => {
  $emit('update:modelValue', (event.target as HTMLInputElement).value)
}

const emitBlur = (event: Event) => {
  $emit('blur', event)
}

// accessibility methods: focus and blur
const focus = () => {
  inputRef.value?.focus()
}

const blur = () => {
  inputRef.value?.blur()
}

// Computed classes
const inputClasses = computed(() => {
  return [
    'block w-full text-base border border-border-primary rounded-lg',
    {
      'pl-10': slots.prefix,
      'pr-10': slots.suffix,
      'border-red-900 focus:border-red-900 bg-red-50 focus:ring-red-900 placeholder-red-700': props.validationStatus === 'error',
      'border-green-500': props.validationStatus === 'success',
      'border-yellow-500': props.validationStatus === 'warning',
      'focus:ring-blue-500 focus:border-blue-500': props.validationStatus === ''
    },
  ];
})
const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'h-8'
    case 'md':
      return 'h-10'
    case 'lg':
      return 'h-[52px]'
    case 'xl':
      return 'h-14'
    default:
      return 'h-10'
  }
})
const readonlyClass = computed(() => {
  return props.readonly ? 'cursor-not-allowed text-gray-500' : 'text-text-primary'
})

export interface BfInputComponent {
  focus: () => void;
  blur: () => void;
}

defineExpose<BfInputComponent>({
  focus,
  blur,
});
</script>